import {reduce, clamp, mapValues} from 'lodash';
import {emptyArray, emptyObject} from '@packages/utilities/fp/utils';
import {getDynListLimits} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {getGuid} from '@packages/systems/core/utils/NativeIdUtils';
import {getMd5Hash} from '@packages/utilities/md5-browser';

export type PageNumbers = {
  [key: string]: number;
};

const getPaginationKeyFromParamName = (paramName: string) => {
  // e.g. `5586d513_page` -> `5586d513`
  const match = paramName.match(/^([a-f0-9]{8})_page$/);
  return match ? match[1] : null;
};

export const getPaginationKeyFromNativeIdPath = (
  nativeIdPath: string[]
): string => {
  const hash = getMd5Hash(nativeIdPath.map(getGuid).join(' '));
  return hash.slice(-8);
};

/**
 * Legacy method for generating pagination keys from native ID paths.
 *
 * This function exists to maintain backward compatibility with URLs pointing to
 * published paginated pages that were published before the pagination key generation
 * was updated to use the full ID path. The legacy method only used a single node ID,
 * which could cause collisions when the published page contains multiple instances
 * of a component that has a collection list.
 *
 * New sites use getPaginationKeyFromNativeIdPath() which hashes the entire ID path,
 * but we keep this function to ensure older URLs continue render the same pagination.
 *
 * @param nativeIdPath - Array of element IDs representing the path to a collection list
 * @returns The last 8 characters of the last element's ID, or null if no ID exists
 */
const getLegacyPaginationKeyFromNativeIdPath = (
  nativeIdPath: string[]
): string | null => {
  const nodeId = nativeIdPath[nativeIdPath.length - 1];

  if (!nodeId) {
    return null;
  }

  // e.g. `5586d513-795e-8353-0bd7-f845c30253e6` -> `c30253e6`
  return getGuid(nodeId).slice(-8);
};

/**
 * Returns every pagination key that can legitimately address the given native
 * ID path: the current full-path hash key and, when it exists, the legacy
 * last-node-ID key.
 *
 * Runtime rendering (getPaginationPageNumber) resolves a page number from
 * either key format for backward compatibility. Any consumer that needs to
 * decide whether an incoming pagination key is "real" for an element (e.g. the
 * SEO canonical/hreflang relevance filter) must accept both formats to stay
 * consistent with what the page actually renders. The current key comes first
 * to mirror the precedence used by getPaginationPageNumber.
 */
export const getPaginationKeysFromNativeIdPath = (
  nativeIdPath: string[]
): string[] => {
  const keys = [getPaginationKeyFromNativeIdPath(nativeIdPath)];
  const legacyKey = getLegacyPaginationKeyFromNativeIdPath(nativeIdPath);
  if (legacyKey !== null && !keys.includes(legacyKey)) {
    keys.push(legacyKey);
  }
  return keys;
};

export const getPaginationPageNumber = (
  pageNumbers: PageNumbers,
  nativeIdPath: string[]
): number => {
  // Try to get the pagination key using the current format
  const paginationKey = getPaginationKeyFromNativeIdPath(nativeIdPath);
  const pageNumber = pageNumbers[paginationKey];

  // If we found a page number with the current format, return it
  if (pageNumber !== undefined) {
    return pageNumber;
  }

  // Otherwise, try the legacy format as a fallback
  const legacyPaginationKey =
    getLegacyPaginationKeyFromNativeIdPath(nativeIdPath);

  // If no legacy key exists, return the default page (1)
  if (legacyPaginationKey === null) {
    return 1;
  }

  // Return the page number from the legacy key, or default to page 1
  return pageNumbers[legacyPaginationKey] || 1;
};

export const buildPageNumberMap = (queryParams: {
  [key: string]: string | null | undefined;
}): Record<string, number> => {
  return reduce(
    queryParams,
    (pageNumberMap: Record<string, number>, paramValue, paramName) => {
      const paginationKey = getPaginationKeyFromParamName(paramName);
      const pageNumber = Number(paramValue);
      if (paginationKey && pageNumber > 1) {
        pageNumberMap[paginationKey] = pageNumber;
      }
      return pageNumberMap;
    },
    {}
  );
};

export const calculatePaginationSkipQueryArg = (
  pageNumber: number,
  itemsPerPage: number,
  initialSkip: number
): number => {
  return pageNumber > 1
    ? initialSkip + (pageNumber - 1) * itemsPerPage
    : initialSkip;
};

export const calculatePaginationLimitQueryArg = (
  pageNumber: number,
  itemsPerPage: number,
  initialLimit: number
): number => {
  const skipToStartOfPage =
    pageNumber > 1 ? (pageNumber - 1) * itemsPerPage : 0;
  const skipToEndOfPage = skipToStartOfPage + itemsPerPage;
  if (skipToStartOfPage > initialLimit) {
    return 0;
  }
  if (skipToEndOfPage > initialLimit) {
    return initialLimit - skipToStartOfPage;
  }
  return itemsPerPage;
};

export const generatePaginationButtonHrefs = ({
  filteredItemsCount,
  skip,
  limit,
  itemsPerPage,
  currentPageNumber,
  pageNumbers,
  paginationKey,
  searchQuery,
}: {
  filteredItemsCount: number;
  skip: number;
  limit: number;
  itemsPerPage: number;
  currentPageNumber: number;
  pageNumbers: PageNumbers;
  paginationKey: string;
  searchQuery: string | null | undefined;
}): {
  previous: string | null;
  next: string | null;
} => {
  const lastPaginationPageNumber = getLastPaginationPageNumber(
    getPaginateableItemsCount(filteredItemsCount, skip, limit),
    itemsPerPage
  );

  return {
    previous: buildPaginationQueryParam(
      getNewPageNumbers(
        pageNumbers,
        paginationKey,
        getPreviousPageNumber(currentPageNumber, lastPaginationPageNumber)
      ),
      searchQuery
    ),
    next: buildPaginationQueryParam(
      getNewPageNumbers(
        pageNumbers,
        paginationKey,
        getNextPageNumber(currentPageNumber, lastPaginationPageNumber)
      ),
      searchQuery
    ),
  };
};

export const getLastPaginationPageNumber = (
  itemsCount: number,
  itemsPerPage: number
): number => {
  return Math.max(Math.ceil(itemsCount / itemsPerPage), 1);
};

export const getPaginateableItemsCount = (
  filteredItemsCount: number,
  skip: number,
  limit: number
): number => {
  const lower = 0;
  const upper = limit;
  return clamp(filteredItemsCount - skip, lower, upper);
};

const getNewPageNumbers = (
  pageNumbers: PageNumbers,
  paginationKey: string,
  newPageNumber: number | null
): PageNumbers => {
  return newPageNumber
    ? {...pageNumbers, [paginationKey]: newPageNumber}
    : emptyObject;
};

export const buildPaginationQueryParam = (
  newPageNumbers: PageNumbers,
  searchQuery?: string | null
): string | null => {
  const paginationKeys = Object.keys(newPageNumbers);
  return paginationKeys.length > 0
    ? `?${paginationKeys
        .sort()
        .reduce(
          (memo, key) => memo.concat(`${key}_page=${newPageNumbers[key]}`),
          searchQuery ? [`query=${searchQuery}`] : emptyArray
        )
        .join('&')}`
    : null;
};

const getPreviousPageNumber = (
  currentPageNumber: number,
  lastPaginationPageNumber: number
): number | null => {
  if (currentPageNumber > lastPaginationPageNumber) {
    return lastPaginationPageNumber;
  }

  return currentPageNumber > 1 ? currentPageNumber - 1 : null;
};

const getNextPageNumber = (
  currentPageNumber: number,
  lastPaginationPageNumber: number
): number | null => {
  if (currentPageNumber >= lastPaginationPageNumber) {
    return null;
  }

  return currentPageNumber + 1;
};

export const capDynamoWrapperLimit =
  (paginationEnabled: boolean) =>
  (isNestedList: boolean) =>
  (limit?: number | null) => {
    if (isNestedList) {
      // If a limit is defined, we assume that it's been set by FE logic that's capping it
      // based on list limits in the UI input code
      if (Number.isFinite(limit) && limit != null && limit > 0) {
        // To prevent anything from accidentally (or maliciously intentionally) setting a large value here,
        // we're limiting it to a max of 100. If we ever roll out >100 for limits, this needs to be updated.
        const REASONABLE_VALIDATION_LIMIT = 100;

        return Math.min(limit, REASONABLE_VALIDATION_LIMIT);
      }
    }

    const {MAX_NESTED_COLLECTION_LIST_LIMIT, MAX_COLLECTION_LIST_LIMIT} =
      getDynListLimits();
    const maxValue = isNestedList
      ? MAX_NESTED_COLLECTION_LIST_LIMIT
      : paginationEnabled
        ? Infinity
        : MAX_COLLECTION_LIST_LIMIT;
    return limit == null || limit < 1 ? maxValue : Math.min(limit, maxValue);
  };

const clampPageNumber = (pageNumber: number) => clamp(pageNumber, 1, 99999);

export const clampPageNumbers = (pageNumbers: {[key: string]: number}) => {
  return mapValues(pageNumbers, clampPageNumber);
};

export const getInitialPaginationLimit = (listItemCount: number): number => {
  if (listItemCount > 25) {
    return 25;
  } else if (listItemCount > 20) {
    return 20;
  } else if (listItemCount > 10) {
    return 10;
  } else if (listItemCount > 5) {
    return 5;
  } else if (listItemCount > 1) {
    return listItemCount - 1;
  } else {
    return 1;
  }
};
